// Learn editor pieces (contracts/learn.md): the claims note, the puzzle-types field, the slides editor with
// "Translate all slides", and the phone preview that draws each layout like the app's story viewer.

import { type Dispatch, type ReactNode, type SetStateAction, useId, useState } from "react";
import { type CtaAction, MAX_PUZZLE_TYPES, MAX_SLIDES, SLIDE_LAYOUTS, type SlideLayout } from "../../api/learn";
import { type ImageRef, LOCALE_LABEL, LOCALES, type Locale, RTL_LOCALES, TRANSLATE_TARGETS, useTranslate } from "../../api/messaging";
import { useCan } from "../../auth/AuthContext";
import { Button, Field, Select, TextInput, cx } from "../../components/ui";
import { KNOWN_TYPES } from "../../lib/catalog";
import {
  duplicateSlide,
  type EditSlide,
  foldSlides,
  LAYOUTS,
  newSlide,
  packSlides,
  puzzleTypeProblem,
  resolveSlide,
  SLIDE_IMAGE,
  type SlideField,
  slideHasLocale,
  slidesEnglishKey,
  STEP_LIMIT,
  STEPS_MAX,
  STEPS_MIN,
} from "../../lib/learn";
import type { LocaleState } from "../../lib/messaging";
import { fmtUsd, ImageField, LocaleTabs, StaleNotice, TranslateError } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import l from "./learn.module.css";

// ---------------------------------------------------------------- the claims rule

/** The claims rule (contracts/learn.md, App Review 1.4.1 / 2.3.1), shown next to the text. */
export function ClaimsNote({ children }: { children: ReactNode }) {
  return (
    <div className={m.notice} data-tone="info" data-testid="claims-note">
      <strong>Claims rule</strong>
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------- puzzle types

/** The types a story teaches (`category.slug`, at most 10), suggested from the admin's catalogue. */
export function PuzzleTypesField({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const listId = useId();
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const p = puzzleTypeProblem(v, value);
    setProblem(p);
    if (p) return;
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <Field label="Puzzle types it teaches" hint={problem ? <span className={m.required}>{problem}</span> : `category.slug, up to ${MAX_PUZZLE_TYPES}. A missed puzzle of these types links to this story.`}>
      <div className={l.types}>
        {value.map((t) => (
          <span key={t} className={l.typeChip} data-testid="puzzle-type">
            <span className="mono">{t}</span>
            <button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))}>
              ×
            </button>
          </span>
        ))}
        {value.length < MAX_PUZZLE_TYPES ? (
          <span className={l.typeAdd}>
            <TextInput
              value={draft}
              list={listId}
              placeholder="pattern.matrix"
              onChange={(e) => {
                setDraft(e.target.value);
                setProblem(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              data-testid="puzzle-type-input"
              aria-label="Add a puzzle type"
            />
            <Button size="sm" variant="ghost" onClick={add} disabled={!draft.trim()}>
              Add
            </Button>
            <datalist id={listId}>
              {KNOWN_TYPES.filter((t) => !value.includes(t)).map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </span>
        ) : null}
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------- slides editor

export interface SlidesTranslation {
  /** slidesEnglishKey at the last translation. */
  from: string | null;
  model: string | null;
  costUsd: number | null;
}

const IMAGE_LABEL = { required: "Image · required", recommended: "Image · recommended", optional: "Image · optional" } as const;

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x!);
  return next;
}

export function SlidesEditor({
  slides,
  onChange,
  locale,
  onLocale,
  selected,
  onSelect,
  translation,
  onTranslation,
}: {
  slides: EditSlide[];
  onChange: Dispatch<SetStateAction<EditSlide[]>>;
  locale: Locale;
  onLocale: (l: Locale) => void;
  selected: number;
  onSelect: (i: number) => void;
  translation: SlidesTranslation;
  onTranslation: (t: SlidesTranslation) => void;
}) {
  const canTranslate = useCan("content.translate");
  const translate = useTranslate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const hasEnglish = slides.some((s) => slideHasLocale(s, "en"));
  const stale = translation.from !== null && translation.from !== slidesEnglishKey(slides);

  const stateOf = (loc: Locale): LocaleState => {
    const withEnglish = slides.filter((s) => slideHasLocale(s, "en"));
    if (withEnglish.length === 0) return "empty";
    const done = withEnglish.filter((s) => slideHasLocale(s, loc)).length;
    return done === 0 ? "empty" : done === withEnglish.length ? "complete" : "partial";
  };
  const translated = TRANSLATE_TARGETS.filter((x) => stateOf(x) !== "empty").length;

  const update = (i: number, fn: (s: EditSlide) => EditSlide) => onChange((list) => list.map((s, j) => (j === i ? fn(s) : s)));
  const setText = (i: number, f: SlideField, v: string) => update(i, (s) => ({ ...s, content: { ...s.content, [locale]: { ...s.content[locale], [f]: v } } }));
  const setSteps = (i: number, steps: string[]) => update(i, (s) => ({ ...s, content: { ...s.content, [locale]: { ...s.content[locale], steps } } }));

  /** Every slide's English in calls of ≤ 40 fields (steps as steps.0, steps.1…), folded back when they all return. */
  const translateAll = async () => {
    const sentFrom = slidesEnglishKey(slides);
    const calls = packSlides(slides);
    setBusy(true);
    setError(null);
    try {
      const results = await Promise.all(calls.map((c) => translate.mutateAsync({ content: { en: c.en }, limits: c.limits })));
      const merged: Partial<Record<Locale, Record<string, string>>> = {};
      for (const r of results) for (const loc of LOCALES) if (loc !== "en" && r.content[loc]) merged[loc] = { ...merged[loc], ...r.content[loc] };
      onChange((list) => foldSlides(list, merged));
      const cost = results.every((r) => r.usage) ? results.reduce((n, r) => n + (r.usage?.costUsd ?? 0), 0) : null;
      onTranslation({ from: sentFrom, model: results[0]?.model ?? null, costUsd: cost });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={l.slides} data-testid="slides-editor">
      <div className={m.localeBar}>
        <LocaleTabs locale={locale} onLocale={onLocale} stateOf={stateOf} label="Slide languages" testPrefix="slides-locale" />
        <div className={m.translateRow}>
          {canTranslate ? (
            <Button size="sm" variant="secondary" busy={busy} disabled={!hasEnglish} onClick={() => void translateAll()} data-testid="translate-slides">
              {translation.model ? "Translate all slides again" : "Translate all slides"}
            </Button>
          ) : null}
          <span className={m.translateMeta} data-testid="slides-translate-meta">
            {translated} of {TRANSLATE_TARGETS.length} languages
            {translation.model ? ` · ${translation.model}${translation.costUsd !== null ? ` · ${fmtUsd(translation.costUsd)}` : ""}` : ""}
          </span>
        </div>
      </div>
      <TranslateError error={error} />
      {stale ? <StaleNotice testId="slides-stale" /> : null}

      <ol className={l.slideList}>
        {slides.map((s, i) => {
          const spec = LAYOUTS[s.layout];
          const f = s.content[locale];
          const en = s.content.en;
          const rtl = RTL_LOCALES.has(locale);
          return (
            <li
              key={s.key}
              className={l.slide}
              data-selected={i === selected || undefined}
              data-dragging={dragFrom === i || undefined}
              onDragOver={(e) => dragFrom !== null && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragFrom !== null) onChange((list) => move(list, dragFrom, i));
                setDragFrom(null);
                onSelect(i);
              }}
              data-testid="slide"
              data-layout={s.layout}
            >
              <div className={l.slideHead} onClick={() => onSelect(i)}>
                <span
                  className={l.handle}
                  draggable
                  onDragStart={(e) => {
                    setDragFrom(i);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={() => setDragFrom(null)}
                  aria-hidden
                  title="Drag to reorder"
                >
                  ⋮⋮
                </span>
                <span className="monoLabel">Slide {i + 1}</span>
                <Select
                  value={s.layout}
                  onChange={(e) => update(i, (x) => ({ ...x, layout: e.target.value as SlideLayout }))}
                  className={l.layoutSelect}
                  aria-label={`Slide ${i + 1} layout`}
                  data-testid="slide-layout"
                >
                  {SLIDE_LAYOUTS.map((x) => (
                    <option key={x} value={x}>
                      {LAYOUTS[x].label}
                    </option>
                  ))}
                </Select>
                <span className={l.slideActions}>
                  <Button size="sm" variant="ghost" aria-label={`Move slide ${i + 1} up`} disabled={i === 0} onClick={() => {
                      onChange((list) => move(list, i, i - 1));
                      onSelect(i - 1);
                    }}>
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Move slide ${i + 1} down`}
                    disabled={i === slides.length - 1}
                    onClick={() => {
                      onChange((list) => move(list, i, i + 1));
                      onSelect(i + 1);
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Duplicate slide ${i + 1}`}
                    disabled={slides.length >= MAX_SLIDES}
                    onClick={() => {
                      onChange((list) => [...list.slice(0, i + 1), duplicateSlide(s), ...list.slice(i + 1)]);
                      onSelect(i + 1);
                    }}
                  >
                    Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Delete slide ${i + 1}`}
                    onClick={() => {
                      onChange((list) => list.filter((_, j) => j !== i));
                      onSelect(Math.max(0, Math.min(selected, slides.length - 2)));
                    }}
                  >
                    Delete
                  </Button>
                </span>
              </div>
              <p className={l.shows}>{spec.shows}</p>
              <div className={l.slideBody}>
                <ImageField
                  image={s.image}
                  onChange={(image) => update(i, (x) => ({ ...x, image }))}
                  recommended={SLIDE_IMAGE}
                  hint="an illustration WITHOUT text (the app draws the words, in 13 languages) · 1080×1350 (4:5) recommended"
                  shape="portrait"
                  label={IMAGE_LABEL[spec.image]}
                  testId={`slide-${i}-image`}
                />
                <div className={l.slideFields}>
                  {spec.fields.map((fs) => (
                    <Field key={fs.key} label={fs.label} hint={`${f[fs.key].length}/${fs.limit}${locale === "en" && spec.required.includes(fs.key) ? " · required" : ""}`}>
                      {fs.rows ? (
                        <textarea
                          className={styles.textarea}
                          rows={fs.rows}
                          maxLength={fs.limit}
                          value={f[fs.key]}
                          dir={rtl ? "rtl" : undefined}
                          lang={locale}
                          placeholder={locale === "en" ? fs.placeholder : en[fs.key] || fs.placeholder}
                          onChange={(e) => setText(i, fs.key, e.target.value)}
                          data-testid={`slide-${fs.key}`}
                        />
                      ) : (
                        <TextInput
                          maxLength={fs.limit}
                          value={f[fs.key]}
                          dir={rtl ? "rtl" : undefined}
                          lang={locale}
                          placeholder={locale === "en" ? fs.placeholder : en[fs.key] || fs.placeholder}
                          onChange={(e) => setText(i, fs.key, e.target.value)}
                          data-testid={`slide-${fs.key}`}
                        />
                      )}
                    </Field>
                  ))}
                  {spec.steps ? <StepsField steps={f.steps} english={en.steps} locale={locale} onChange={(steps) => setSteps(i, steps)} /> : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className={l.addRow} data-testid="add-slide">
        <span className="monoLabel">{slides.length >= MAX_SLIDES ? `${MAX_SLIDES} slides: the most a story takes` : "Add a slide"}</span>
        {SLIDE_LAYOUTS.map((x) => (
          <Button
            key={x}
            size="sm"
            variant="secondary"
            disabled={slides.length >= MAX_SLIDES}
            onClick={() => {
              onChange((list) => [...list, newSlide(x)]);
              onSelect(slides.length);
            }}
            title={LAYOUTS[x].shows}
          >
            + {LAYOUTS[x].label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** 2–4 numbered steps (≤ 90 each); in another language, an empty list falls back to the English steps. */
function StepsField({ steps, english, locale, onChange }: { steps: string[]; english: string[]; locale: Locale; onChange: (steps: string[]) => void }) {
  const rows = locale === "en" ? steps : steps.length ? steps : english.map(() => "");
  const filled = steps.filter((x) => x.trim()).length;
  const englishCount = english.filter((x) => x.trim()).length;
  // A language with steps shows only its own: half-translated steps would cut the list short.
  const short = locale !== "en" && filled > 0 && filled < englishCount;
  return (
    <div className={l.steps}>
      <span className="monoLabel">
        Steps · {STEPS_MIN}–{STEPS_MAX}
        {locale === "en" && filled < STEPS_MIN ? <span className={m.required}> · at least {STEPS_MIN}</span> : null}
      </span>
      {rows.map((v, j) => (
        <div key={j} className={l.step}>
          <span className={l.stepNum}>{j + 1}</span>
          <TextInput
            value={v}
            maxLength={STEP_LIMIT}
            dir={RTL_LOCALES.has(locale) ? "rtl" : undefined}
            lang={locale}
            placeholder={locale === "en" ? `Step ${j + 1}` : english[j] || `Step ${j + 1}`}
            onChange={(e) => onChange(rows.map((x, k) => (k === j ? e.target.value : x)))}
            aria-label={`Step ${j + 1}`}
            data-testid="slide-step"
          />
          <Button size="sm" variant="ghost" aria-label={`Remove step ${j + 1}`} onClick={() => onChange(rows.filter((_, k) => k !== j))}>
            ×
          </Button>
        </div>
      ))}
      {short ? (
        <span className={m.required} data-testid="steps-short">
          Translate every step, or leave them all empty to show the English ones.
        </span>
      ) : null}
      {rows.length < STEPS_MAX ? (
        <Button size="sm" variant="ghost" onClick={() => onChange([...rows, ""])} data-testid="add-step">
          + Step
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the phone preview

/**
 * The story viewer as the app draws it (contracts/learn.md "App"): segmented progress bars on top, the slide's
 * image with the text the app lays over it, and the button on the last slide. Taps on the left or right half
 * go back or forward, like the app.
 */
export function StoryPreview({
  title,
  slides,
  index,
  onIndex,
  locale,
  onLocale,
  cta,
  ctaAction,
  cover,
}: {
  title: string;
  slides: EditSlide[];
  index: number;
  onIndex: (i: number) => void;
  locale: Locale;
  onLocale: (l: Locale) => void;
  cta: string;
  ctaAction: CtaAction;
  cover: ImageRef | null;
}) {
  const n = slides.length;
  const i = Math.min(index, Math.max(0, n - 1));
  const slide = slides[i];
  const rtl = RTL_LOCALES.has(locale);
  return (
    <div className={l.preview} data-testid="story-preview">
      <div className={m.previewHead}>
        <span className="monoLabel">Preview</span>
        <Select value={locale} onChange={(e) => onLocale(e.target.value as Locale)} className={l.previewLocale} aria-label="Preview language" data-testid="preview-locale">
          {LOCALES.map((x) => (
            <option key={x} value={x}>
              {LOCALE_LABEL[x]}
            </option>
          ))}
        </Select>
      </div>
      <div className={l.circleRow} aria-label="Home row preview">
        <span className={l.circle} data-unseen>
          {cover ? <img src={cover.url} alt="" /> : <span className={l.circleEmpty}>cover</span>}
        </span>
        <span className={l.circleTitle}>{title || "Title"}</span>
      </div>
      <div className={l.phone} dir={rtl ? "rtl" : undefined}>
        <div className={l.bars} aria-label={n ? `Slide ${i + 1} of ${n}` : "No slides"}>
          {slides.map((s, j) => (
            <span key={s.key} data-done={j <= i || undefined} />
          ))}
        </div>
        <div className={l.closeX} aria-hidden>
          ×
        </div>
        {slide ? (
          <SlideView slide={slide} locale={locale} last={i === n - 1} cta={ctaAction !== "none" ? cta || "Button" : null} />
        ) : (
          <div className={l.noSlides}>Add a slide to see it here.</div>
        )}
        <button type="button" className={l.tapLeft} aria-label="Previous slide" disabled={i === 0} onClick={() => onIndex(i - 1)} />
        <button type="button" className={l.tapRight} aria-label="Next slide" disabled={i >= n - 1} onClick={() => onIndex(i + 1)} />
      </div>
      <div className={l.previewNav}>
        <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => onIndex(i - 1)} data-testid="preview-prev">
          ‹ Prev
        </Button>
        <span className="mono" data-testid="preview-position">
          {n ? `${i + 1} / ${n}` : "0 / 0"}
        </span>
        <Button size="sm" variant="ghost" disabled={i >= n - 1} onClick={() => onIndex(i + 1)} data-testid="preview-next">
          Next ›
        </Button>
      </div>
    </div>
  );
}

function SlideView({ slide, locale, last, cta }: { slide: EditSlide; locale: Locale; last: boolean; cta: string | null }) {
  const t = resolveSlide(slide, locale);
  const img = slide.image ? <img src={slide.image.url} alt="" className={l.slideImage} /> : null;
  const button = last && cta ? <span className={l.cta}>{cta}</span> : null;
  /** The slide's content, centred vertically when there is no image; the button sits at the bottom. */
  const stack = (content: ReactNode, centered = false) => (
    <div className={l.layoutStack}>
      <div className={cx(l.main, (centered || !slide.image) && l.middle, centered && l.centered)}>{content}</div>
      {button}
    </div>
  );
  const body = (() => {
    switch (slide.layout) {
      case "image":
        return (
          <div className={l.layoutImage}>
            {slide.image ? <img src={slide.image.url} alt="" className={l.bleed} /> : <span className={l.missingImage}>Image needed</span>}
            <div className={l.caption}>
              <p>{t.text}</p>
              {button}
            </div>
          </div>
        );
      case "image_text":
        return stack(
          <>
            {img ?? <span className={cx(l.slideImage, l.missingImage)}>Image recommended</span>}
            <div className={l.textBlock}>
              <h4>{t.headline}</h4>
              <p>{t.text}</p>
            </div>
          </>,
        );
      case "big_stat":
        return stack(
          <>
            {img}
            <div className={l.stat}>{t.stat}</div>
            <div className={l.textBlock}>
              <h4>{t.headline}</h4>
              <p>{t.text}</p>
            </div>
          </>,
          true,
        );
      case "tip":
        return stack(
          <>
            {img}
            <div className={l.textBlock}>
              <span className={l.tipLabel}>Tip</span>
              <h4>{t.headline}</h4>
              <p>{t.text}</p>
            </div>
          </>,
        );
      case "steps":
        return stack(
          <>
            {img}
            <div className={l.textBlock}>
              <h4>{t.headline}</h4>
              <ol className={l.stepList}>
                {t.steps.map((x, j) => (
                  <li key={j}>
                    <span>{j + 1}</span>
                    {x}
                  </li>
                ))}
              </ol>
            </div>
          </>,
        );
      case "quote":
        return stack(
          <>
            {img}
            <blockquote className={l.quote}>
              <p>“{t.text}”</p>
              {t.source ? <cite>— {t.source}</cite> : null}
            </blockquote>
          </>,
          true,
        );
    }
  })();
  return (
    <div className={l.slideView} data-testid="preview-slide" data-layout={slide.layout}>
      {body}
    </div>
  );
}
